import { initializeAnalytics, isSupported, logEvent, setConsent, setAnalyticsCollectionEnabled } from 'firebase/analytics';

const CONSENT_KEY = 'convivial.analytics.v1';
let instance;
let initializing;
let consentVersion = 0;

// Only static screen names leave the app: never IDs, query strings or login tokens.
const routes = [
  [/^\/$/, 'home'], [/^\/index\/?$/, 'discover'],
  [/^\/table\/?$/, 'table_create'], [/^\/(table|groups)\/[^/]+\/chat\/?$/, 'table_chat'],
  [/^\/(table|groups)\/[^/]+\/?$/, 'table_detail'],
  [/^\/restaurants\/[^/]+\/?$/, 'restaurant'],
  [/^\/user(\/[^/]+)?\/tables\/?$/, 'my_tables'],
  [/^\/user(\/[^/]+)?\/?$/, 'profile'],
  [/^\/feedback\/[^/]+\/?$/, 'feedback'],
  [/^\/feedbacks-received(\/[^/]+)?\/?$/, 'feedback_received'],
  [/^\/feedbacks-sent\/?$/, 'feedback_sent'],
  ...['login', 'flutter-web-login', 'welcome', 'tutorial', 'settings', 'privacy-preferences',
    'legal-consents', 'notifications', 'notification', 'more-info'].map((path) => [
    new RegExp(`^/${path}/?$`), path.replace(/-/g, '_'),
  ]),
];
export function screenForPath(path) {
  return routes.find(([pattern]) => pattern.test(path))?.[1] || 'other';
}
const screens = new Set([...routes.map(([, name]) => name), 'other']);
const actions = new Set(['nav_home', 'nav_notifications', 'nav_create', 'nav_profile', 'nav_settings',
  'login_google', 'login_apple', 'create_table', 'join_table', 'leave_table', 'update_table',
  'cancel_table', 'share_table', 'open_chat', 'save_profile', 'submit_feedback', 'send_message',
  'button_other', 'link_other', 'select_other', 'checkbox_other', 'tab_other']);
const events = new Set(['page_view', 'screen_engagement', 'ui_click', 'action_start', 'scroll_depth', 'form_start',
  'form_field', 'form_abandon', 'form_error', 'login_start', 'login', 'login_error', 'logout',
  'table_created', 'table_joined', 'table_left', 'table_updated', 'table_cancelled',
  'profile_saved', 'feedback_submitted', 'chat_message_sent']);

export function getAnalyticsConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

function consentSettings(enabled) {
  return { analytics_storage: enabled ? 'granted' : 'denied', ad_storage: 'denied',
    ad_user_data: 'denied', ad_personalization: 'denied' };
}

export function saveAnalyticsConsent(enabled) {
  try { localStorage.setItem(CONSENT_KEY, enabled ? 'granted' : 'denied'); } catch { /* Storage may be blocked. */ }
  synchronizeAnalyticsConsent();
  window.dispatchEvent(new Event('convivial:analytics-consent'));
}

export function synchronizeAnalyticsConsent() {
  consentVersion += 1;
  const enabled = getAnalyticsConsent() === 'granted';
  if (instance) {
    setAnalyticsCollectionEnabled(instance, enabled);
    setConsent(consentSettings(enabled));
  }
}

async function getInstance() {
  if (instance) return instance;
  if (!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) return null;
  if (!initializing) {
    initializing = isSupported().then(async (supported) => {
      if (!supported || getAnalyticsConsent() !== 'granted') return null;
      const { app } = await import('../firebase');
      if (getAnalyticsConsent() !== 'granted') return null;
      setConsent(consentSettings(true));
      instance = initializeAnalytics(app, { config: {
        send_page_view: false,
        page_location: `${window.location.origin}/`,
        page_referrer: referrerOrigin(),
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      } });
      return instance;
    }).catch(() => null).finally(() => { initializing = null; });
  }
  return initializing;
}

function referrerOrigin() {
  try {
    const url = new URL(document.referrer);
    return ['https:', 'http:'].includes(url.protocol) ? url.origin : '';
  } catch { return ''; }
}

// A small allowlist prevents accidentally adding form values or error messages.
export function safeParameters(params) {
  const safe = {};
  for (const [key, value] of Object.entries(params)) {
    if (['screen_name', 'previous_screen'].includes(key) && screens.has(value)) safe[key] = value;
    if (key === 'action' && actions.has(value)) safe[key] = value;
    if (key === 'form_name' && ['profile', 'table_create', 'table_detail', 'feedback', 'table_chat'].includes(value)) safe[key] = value;
    if (key === 'field_type' && ['text', 'textarea', 'select', 'checkbox', 'radio', 'number', 'date', 'file', 'other'].includes(value)) safe[key] = value;
    if (key === 'method' && ['google', 'apple', 'native'].includes(value)) safe[key] = value;
    if (key === 'auth_state' && ['signed_in', 'guest'].includes(value)) safe[key] = value;
    if (key === 'app_surface' && ['web', 'native_webview'].includes(value)) safe[key] = value;
    if (['visible_time_msec', 'percent_scrolled'].includes(key) && Number.isFinite(value) && value >= 0) safe[key] = Math.round(value);
  }
  return safe;
}

export async function trackEvent(name, params = {}) {
  if (!events.has(name) || getAnalyticsConsent() !== 'granted') return;
  const version = consentVersion;
  const base = process.env.REACT_APP_BASE_PATH || '';
  const path = window.location.pathname.slice(base.length) || '/';
  // Snapshot metadata before initialization, which can finish after navigation.
  const safe = safeParameters({ screen_name: screenForPath(path), ...params });
  window.dispatchEvent(new CustomEvent('convivial:analytics-event', { detail: { name } }));
  try {
    const analytics = await getInstance();
    if (!analytics || version !== consentVersion || getAnalyticsConsent() !== 'granted') return;
    logEvent(analytics, name, {
      ...safe,
      app_surface: window.__CONVIVIAL_NATIVE_APP__ ? 'native_webview' : 'web',
      page_title: safe.screen_name || 'Convivial',
      page_location: `${window.location.origin}/${safe.screen_name || 'app'}`,
      page_referrer: referrerOrigin(),
    });
  } catch { /* Analytics must never interrupt a user action. */ }
}
